import { getAiApiUrl } from '../../config';
import { storage } from '../../lib/storage';
import { notify } from '../../utils/toast';
import { Conversation } from '../../types/chat';
// import { getAvatarUrl } from '../../utils/avatar';
import { Message } from "../../types/chat";
/**
 * Fetches conversations shared to the current user
 * @param auth Authentication data
 * @returns Array of conversation IDs shared to the user
 */
export const fetchSharedToConversations = async (
    auth: { jwt: string; walletAddress: string; nonce: string; did: string }
): Promise<any[]> => {
    try {
        // Fetch shared-to conversations from API
        const response = await fetch(`${getAiApiUrl()}/shared-to-conversations`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${auth.jwt}`
            },
            body: JSON.stringify({
                walletAddress: auth.walletAddress,
                nonce: auth.nonce,
                user: auth.did.toLowerCase().replaceAll('_uuid', ':uuid').replaceAll('_addr', ':addr').replaceAll('_asa', ':asa').replaceAll('_app', ':app').replaceAll('_txn', ':txn')
            })
        });

        if (!response.ok) {
            throw new Error('Failed to fetch shared-to conversations');
        }

        const data = await response.json();
        return data.shared_to_conversations || [];
    } catch (error: any) {
        console.error('Error fetching shared-to conversations:', error);
        return [];
    }
};

/**
 * Fetches conversations shared by the current user
 * @param auth Authentication data
 * @returns Array of conversation IDs shared by the user
 */
export const fetchSharedByConversations = async (
    auth: { jwt: string; walletAddress: string; nonce: string; did: string }
): Promise<string[]> => {
    try {
        // Fetch shared-by conversations from API
        const response = await fetch(`${getAiApiUrl()}/shared-conversations`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${auth.jwt}`
            },
            body: JSON.stringify({
                walletAddress: auth.walletAddress,
                nonce: auth.nonce,
                user: auth.did.toLowerCase().replaceAll('_uuid', ':uuid').replaceAll('_addr', ':addr').replaceAll('_asa', ':asa').replaceAll('_app', ':app').replaceAll('_txn', ':txn')
            })
        });

        if (!response.ok) {
            throw new Error('Failed to fetch shared-by conversations');
        }

        const data = await response.json();
        return data.shared_conversations?.map((obj: any) => obj.key) || [];
    } catch (error: any) {
        console.error('Error fetching shared-by conversations:', error);
        return [];
    }
};

// Function to fetch shared conversation messages
export const fetchSharedConversation = async (conversation_id: string, auth: { jwt: string; walletAddress: string; nonce: string; did: string }) => {
    try {
        const response = await fetch(`${getAiApiUrl()}/shared-convo`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${auth.jwt}`
            },
            body: JSON.stringify({
                walletAddress: auth.walletAddress,
                nonce: auth.nonce,
                conversation_id: conversation_id,
                user: auth.did.toLowerCase().replaceAll('_uuid', ':uuid').replaceAll('_addr', ':addr').replaceAll('_asa', ':asa').replaceAll('_app', ':app').replaceAll('_txn', ':txn')
            })
        });

        if (!response.ok) {
            const errorMessage = await response.json();
            throw new Error(errorMessage.error || 'Failed to fetch shared conversation');
        }

        const data = await response.json();
        
        notify.success('Shared conversation from  ' + `${data.shared_by.slice(0,4)}...${data.shared_by.slice(-4)}`);
        // const profile = await storage.getProfile(auth.walletAddress);
        // const avatarUrl = profile ? getAvatarUrl(profile) : undefined;

        // Create two messages for each API record - one for user query and one for assistant answer
        const formattedMessages: Message[] = [];
        data.conversation.messages.forEach((msg: any) => {
            // Add user message
            if (msg.query) {
                formattedMessages.push({
                    id: `${msg.id}-user`,
                    content: msg.query,
                    sender: 'user',
                    created_at: new Date(msg.created_at * 1000).toISOString(),
                    metadata: msg.metadata || undefined,
                    // avatar: avatarUrl
                });
            }

            // Add assistant message
            if (msg.answer) {
                formattedMessages.push({
                    id: `${msg.id}`,
                    content: msg.answer,
                    sender: 'assistant',
                    created_at: new Date(msg.created_at * 1000).toISOString(),
                    metadata: msg.metadata || undefined,
                    avatar: '/images/Agent.png'
                });
            }
        });


        return formattedMessages;
    } catch (error: any) {
        console.error('Error loading shared conversation:', error);
        notify.error(error.message || 'Failed to load shared conversation');
        return null;
    }
}

/**
 * Updates the local storage with shared conversation information
 * @param walletAddress User's wallet address
 * @param sharedToIds Conversation IDs shared to the user
 * @param sharedByIds Conversation IDs shared by the user
 * @param conversations Current conversations list
 * @returns Updated conversations list with shared flags
 */
export const updateSharedConversations = async (
    walletAddress: string,
    sharedToIds: any[],
    sharedByIds: string[],
    conversations: Conversation[]
): Promise<Conversation[]> => {
    try {
        // Create sets for faster lookups

        const sharedBySet = new Set(sharedByIds);

        // Update each conversation with shared flags
        const updatedConversations = await Promise.all(
            conversations.map(async (conversation) => {

                const isSharedBy = sharedBySet.has(conversation.id);

                // Only update if the shared status has changed
                if (conversation.isSharedBy !== isSharedBy) {
                    const updatedConversation = {
                        ...conversation,

                        isSharedBy
                    };

                    // Update in local storage
                    await storage.updateConversation(walletAddress, conversation.id, {

                        isSharedBy
                    });

                    return updatedConversation;
                }

                return conversation;
            })
        );
        for (let index = 0; index < sharedToIds.length; index++) {
            const conversation = sharedToIds[index];
            conversation.isSharedTo = true;
            conversation.title = conversation.name;
            
            // Convert Unix timestamps to ISO strings if they exist
            if (conversation.created_at && typeof conversation.created_at === 'number') {
                conversation.created_at = new Date(conversation.created_at * 1000).toISOString();
            } else if (!conversation.created_at) {
                // Only set current time if no timestamp exists
                conversation.created_at = new Date().toISOString();
            }
            
            if (conversation.updated_at && typeof conversation.updated_at === 'number') {
                conversation.updated_at = new Date(conversation.updated_at * 1000).toISOString();
            } else if (!conversation.updated_at) {
                // Only set current time if no timestamp exists
                conversation.updated_at = new Date().toISOString();
            }
            
            updatedConversations.push(conversation);
        }
        return updatedConversations;
    } catch (error) {
        console.error('Error updating shared conversations:', error);
        return conversations;
    }
};
